import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import _ from 'lodash';
import { useQueryState } from 'nuqs';
import { TbChevronRight } from 'react-icons/tb';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { resolveSearchMode, searchMatchModeQuery, searchModeQuery, searchQuery, type SearchMode } from '@/queries';
import {
    AnimatedSearchSiderPanel,
    defaultFilters,
    getSearchFilterJoin,
    searchFiltersQuery,
    SearchSiderToggleRow,
    serializeSearchFiltersQueryValue,
    SmallResourceFooter,
    SmallResourceFooterLayout,
    unwrapSearchFilters,
    updateSearchFilterEnvelope,
} from '@/shared';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';

import { Header } from './layout';
import KeyboardShortcutsWrapper from './layout/00-KeyboardShortcutsWrapper';
import SearchTabs from './layout/02-SearchTabs';
import AllSearch, { FederatedSearchView } from './layout/02-SearchTabs/routes/00-All';
import AllSearchFilters from './layout/02-SearchTabs/routes/00-All/components/AllSearchFilters';
import { ALL_SEARCH_DOMAINS } from './layout/02-SearchTabs/routes/00-All/useAllSearch';
import Metadata from './layout/02-SearchTabs/routes/01-Workspace/meta';
import Companies from './layout/02-SearchTabs/routes/02-Companies/01-CompaniesSearch';
import FiltersRow from './layout/02-SearchTabs/routes/02-Companies/01-CompaniesSearch/layout/02-FiltersRow';
import MarketCalendar from './layout/02-SearchTabs/routes/05-Events/components/05-Calendar';
import type {
    CalendarEventSourceId,
    CalendarSelection,
    EconomicEventImpact,
} from './layout/02-SearchTabs/routes/05-Events/components/05-Calendar/calendarEvents';
import CalendarSelectedDaySider from './layout/02-SearchTabs/routes/05-Events/components/05-Calendar/CalendarSelectedDaySider';
// Global Trade remains source-resident while its production Search surface is intentionally disabled.
// import GlobalTrade from './layout/02-SearchTabs/routes/07-GlobalTrade';
import SearchInput from './layout/03-SearchInput';
import SearchResultsFooter from './layout/04-SearchResultsFooter';
import { resolveSearchPreferences, SearchPreferences } from './preferences';
import SearchCompanySyncProgressProvider from './SearchCompanySyncProgress';
import type { SearchResultsFooterSnapshot } from './SearchResultsFooterState';
import { searchTabDefinitions } from './searchTabs';

const SCOPED_SEARCH_DOMAINS = {
    documents: ['documents'],
    economic: ['economic'],
    news: ['news'],
    people: ['people'],
    workspace: ['workspace'],
} as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
const stringArray = (value: unknown) =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const pruneEmpty = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        return value
            .map(pruneEmpty)
            .filter(
                (item) => !(Array.isArray(item) && item.length === 0) && !(isRecord(item) && !Object.keys(item).length),
            );
    }
    if (isRecord(value)) {
        return Object.fromEntries(
            Object.entries(value)
                .map(([key, item]) => [key, pruneEmpty(item)] as const)
                .filter(
                    ([, item]) =>
                        item !== undefined &&
                        !(Array.isArray(item) && item.length === 0) &&
                        !(isRecord(item) && !Object.keys(item).length),
                ),
        );
    }

    return value;
};

const SearchContent: FC = (): JSX.Element => {
    const url = '/search';
    const location = useLocation();
    const { token } = useAntDesignTheme();
    const { hasMinimumPlan } = useEntitlements();
    const { preferences } = useUserPreferences();
    const searchPreferences = resolveSearchPreferences(preferences?.search as SearchPreferences | undefined);
    const canUseAdvancedSearch = hasMinimumPlan('pro');

    // const [input, setInput] = useState('');
    const [input, setInput] = useQueryState('q', searchQuery);
    const [matchMode, setMatchMode] = useQueryState('match', searchMatchModeQuery);
    const [requestedSearchMode, setRequestedSearchMode] = useQueryState('mode', searchModeQuery);
    const [filters, setFilters] = useQueryState('filters', searchFiltersQuery);
    const defaultSearchMode = resolveSearchMode(null, canUseAdvancedSearch);
    const searchMode = resolveSearchMode(requestedSearchMode, canUseAdvancedSearch);
    const setSearchMode = useCallback(
        (nextMode: SearchMode) => {
            const permittedMode = resolveSearchMode(nextMode, canUseAdvancedSearch);
            void setRequestedSearchMode(permittedMode === defaultSearchMode ? null : permittedMode);
        },
        [canUseAdvancedSearch, defaultSearchMode, setRequestedSearchMode],
    );

    useEffect(() => {
        if (!canUseAdvancedSearch && requestedSearchMode && requestedSearchMode !== 'keyword') {
            void setRequestedSearchMode(null);
        }
    }, [canUseAdvancedSearch, requestedSearchMode, setRequestedSearchMode]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const existingFilters = searchParams.get('filters');

        if (existingFilters === null) {
            return;
        }

        const canonicalFilters = serializeSearchFiltersQueryValue(filters);

        if (canonicalFilters === '{}') {
            searchParams.delete('filters');
        } else if (existingFilters === canonicalFilters) {
            return;
        } else {
            searchParams.set('filters', canonicalFilters);
        }

        const nextSearch = searchParams.toString();
        window.history.replaceState(
            window.history.state,
            '',
            `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash}`,
        );
    }, [filters, location.hash, location.pathname, location.search]);

    const handleChange = useCallback(
        (path: string[], value: unknown) => {
            setFilters((prev: unknown) =>
                updateSearchFilterEnvelope(prev, (currentFilters) => {
                    const updatedFilters = _.cloneDeep(currentFilters);

                    if (_.isEqual(value, _.get(defaultFilters, path))) {
                        _.unset(updatedFilters, path);
                    } else {
                        _.set(updatedFilters, path, value);
                    }

                    const prunedFilters = pruneEmpty(updatedFilters);

                    return isRecord(prunedFilters) ? prunedFilters : {};
                }),
            );
        },
        [setFilters],
    );

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [calendarSelection, setCalendarSelection] = useState<CalendarSelection>();
    const [leftSiderCollapsed, setLeftSiderCollapsed] = useState(true);
    const [rightSiderCollapsed, setRightSiderCollapsed] = useState(false);
    const [resultsFooter, setResultsFooter] = useState<SearchResultsFooterSnapshot | null>(null);
    const toggleLeftSider = useCallback(() => setLeftSiderCollapsed((collapsed) => !collapsed), []);
    const toggleRightSider = useCallback(() => setRightSiderCollapsed((collapsed) => !collapsed), []);
    const reportResultsFooter = useCallback((snapshot: SearchResultsFooterSnapshot | null) => {
        setResultsFooter((previousSnapshot) => {
            if (!snapshot) {
                return null;
            }

            if (snapshot.loading && previousSnapshot?.elapsedMs !== undefined) {
                return {
                    ...snapshot,
                    elapsedMs: previousSnapshot.elapsedMs,
                    pageSize: previousSnapshot.pageSize,
                    total: previousSnapshot.total,
                };
            }

            return snapshot;
        });
    }, []);
    const resolvedSearchFilters = unwrapSearchFilters(filters);
    const filterJoin = getSearchFilterJoin(filters);
    const searchDomainFilters = isRecord(resolvedSearchFilters.searchDomains)
        ? resolvedSearchFilters.searchDomains
        : {};
    const eventFilterRecord = isRecord(searchDomainFilters.events) ? searchDomainFilters.events : {};
    const calendarSourceFilters = useMemo(
        () =>
            stringArray(eventFilterRecord.sources).filter(
                (source): source is CalendarEventSourceId => source === 'dividend' || source === 'economic',
            ),
        [eventFilterRecord.sources],
    );
    const calendarImpactFilters = useMemo(
        () =>
            stringArray(eventFilterRecord.impacts).filter((impact): impact is EconomicEventImpact =>
                ['high', 'medium', 'low', 'unknown'].includes(impact),
            ),
        [eventFilterRecord.impacts],
    );
    const calendarResearchFilters = useMemo(
        () => ({
            coverage: typeof eventFilterRecord.coverage === 'string' ? eventFilterRecord.coverage : 'research',
            countries: stringArray(eventFilterRecord.countries),
            currencies: stringArray(eventFilterRecord.currencies),
            symbols: stringArray(eventFilterRecord.symbols),
        }),
        [
            eventFilterRecord.coverage,
            eventFilterRecord.countries,
            eventFilterRecord.currencies,
            eventFilterRecord.symbols,
        ],
    );
    const activeSearchTabDefinition = searchTabDefinitions.find(
        (tab) => tab.key === (location.pathname.split('/')[2] ?? 'all'),
    );
    const activeSearchTab = activeSearchTabDefinition?.key ?? 'all';
    const activeSearchTabLabel = activeSearchTabDefinition?.label ?? 'Search';
    const isSearchResultsRoute = location.pathname.split('/').filter(Boolean).length === 2;
    const activeFilterScopePaths =
        activeSearchTab === 'all'
            ? ALL_SEARCH_DOMAINS.map((domain) => ['searchDomains', domain])
            : activeSearchTab === 'companies'
              ? []
              : ALL_SEARCH_DOMAINS.includes(activeSearchTab as (typeof ALL_SEARCH_DOMAINS)[number])
                ? [['searchDomains', activeSearchTab]]
                : [];

    const footer = (
        <SmallResourceFooter
            left={<SearchResultsFooter snapshot={resultsFooter} />}
            center={
                <span
                    style={{
                        alignItems: 'center',
                        color: token.colorTextTertiary,
                        display: 'inline-flex',
                        gap: '0.25rem',
                    }}
                >
                    <small>Search</small>
                    <TbChevronRight aria-hidden size='0.75rem' />
                    <small>{activeSearchTabLabel}</small>
                </span>
            }
        />
    );

    return (
        <SmallResourceFooterLayout footer={footer}>
            <Metadata />
            <KeyboardShortcutsWrapper filters={filters} setFilters={setFilters}>
                <Header />
                <SearchTabs setInput={setInput} clearFilters={() => setFilters(null)} />
                <SearchInput
                    input={input}
                    setInput={setInput}
                    matchMode={matchMode}
                    searchMode={searchMode}
                    setMatchMode={setMatchMode}
                    setSearchMode={setSearchMode}
                    selectedIndex={selectedIndex}
                    setSelectedIndex={setSelectedIndex}
                />
                {isSearchResultsRoute ? (
                    <FiltersRow
                        handleChange={handleChange}
                        includeRoot={activeSearchTab === 'companies'}
                        scopePaths={activeFilterScopePaths}
                        tab={activeSearchTab}
                    />
                ) : null}
                <div style={{ flex: '1 1 0%', minHeight: 0, overflow: 'hidden' }}>
                    <Routes>
                        <Route index element={<Navigate to={`${url}/${searchPreferences.defaultTab}`} replace />} />
                        <Route
                            path='all'
                            element={
                                <AllSearch
                                    filters={filters}
                                    handleChange={handleChange}
                                    leftSiderCollapsed={leftSiderCollapsed}
                                    matchMode={matchMode}
                                    onToggleLeftSider={toggleLeftSider}
                                    onToggleRightSider={toggleRightSider}
                                    onResultsFooterChange={reportResultsFooter}
                                    query={input}
                                    rightSiderCollapsed={rightSiderCollapsed}
                                    searchMode={searchMode}
                                />
                            }
                        />
                        <Route
                            path='workspace'
                            element={
                                <FederatedSearchView
                                    domains={SCOPED_SEARCH_DOMAINS.workspace}
                                    filters={filters}
                                    handleChange={handleChange}
                                    leftSiderCollapsed={leftSiderCollapsed}
                                    matchMode={matchMode}
                                    onToggleLeftSider={toggleLeftSider}
                                    onToggleRightSider={toggleRightSider}
                                    onResultsFooterChange={reportResultsFooter}
                                    query={input}
                                    rightSiderCollapsed={rightSiderCollapsed}
                                />
                            }
                        />
                        <Route
                            path='people'
                            element={
                                <FederatedSearchView
                                    domains={SCOPED_SEARCH_DOMAINS.people}
                                    filters={filters}
                                    handleChange={handleChange}
                                    leftSiderCollapsed={leftSiderCollapsed}
                                    matchMode={matchMode}
                                    onToggleLeftSider={toggleLeftSider}
                                    onToggleRightSider={toggleRightSider}
                                    onResultsFooterChange={reportResultsFooter}
                                    query={input}
                                    rightSiderCollapsed={rightSiderCollapsed}
                                />
                            }
                        />
                        <Route
                            path='documents'
                            element={
                                <FederatedSearchView
                                    domains={SCOPED_SEARCH_DOMAINS.documents}
                                    filters={filters}
                                    handleChange={handleChange}
                                    leftSiderCollapsed={leftSiderCollapsed}
                                    matchMode={matchMode}
                                    onToggleLeftSider={toggleLeftSider}
                                    onToggleRightSider={toggleRightSider}
                                    onResultsFooterChange={reportResultsFooter}
                                    query={input}
                                    rightSiderCollapsed={rightSiderCollapsed}
                                />
                            }
                        />
                        <Route
                            path='news'
                            element={
                                <FederatedSearchView
                                    domains={SCOPED_SEARCH_DOMAINS.news}
                                    filters={filters}
                                    handleChange={handleChange}
                                    leftSiderCollapsed={leftSiderCollapsed}
                                    matchMode={matchMode}
                                    onToggleLeftSider={toggleLeftSider}
                                    onToggleRightSider={toggleRightSider}
                                    onResultsFooterChange={reportResultsFooter}
                                    query={input}
                                    rightSiderCollapsed={rightSiderCollapsed}
                                />
                            }
                        />
                        <Route
                            path='events'
                            element={
                                <Row
                                    style={{
                                        boxSizing: 'border-box',
                                        height: '100%',
                                        minHeight: 0,
                                        overflow: 'hidden',
                                    }}
                                    wrap={false}
                                >
                                    <AnimatedSearchSiderPanel
                                        open={!leftSiderCollapsed}
                                        padding='0.5rem 0 0'
                                        siderLabel='Search filters'
                                        style={{ height: '100%', minHeight: 0 }}
                                        width='20.833333%'
                                    >
                                        <AllSearchFilters
                                            domains={['events']}
                                            filters={filters}
                                            handleChange={handleChange}
                                        />
                                    </AnimatedSearchSiderPanel>
                                    <Col
                                        flex='auto'
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            height: '100%',
                                            minHeight: 0,
                                            minWidth: 0,
                                            overflow: 'hidden',
                                            padding: 0,
                                        }}
                                    >
                                        <div style={{ flex: '1 1 0%', minHeight: 0, overflow: 'hidden' }}>
                                            <MarketCalendar
                                                researchFilters={calendarResearchFilters}
                                                sourcesDisabled={stringArray(eventFilterRecord.sources).includes(
                                                    'none',
                                                )}
                                                onSourceFiltersChange={(sources) =>
                                                    handleChange(
                                                        ['searchDomains', 'events', 'sources'],
                                                        sources.length ? sources : ['none'],
                                                    )
                                                }
                                                filterJoin={filterJoin}
                                                impactFilters={calendarImpactFilters}
                                                onSelectEvents={setCalendarSelection}
                                                renderToolbar={(toolbar) => (
                                                    <SearchSiderToggleRow
                                                        style={{ margin: 0 }}
                                                        leftSiderCollapsed={leftSiderCollapsed}
                                                        onToggleLeftSider={toggleLeftSider}
                                                        onToggleRightSider={toggleRightSider}
                                                        rightSiderAvailable
                                                        rightSiderCollapsed={rightSiderCollapsed}
                                                    >
                                                        {toolbar}
                                                    </SearchSiderToggleRow>
                                                )}
                                                sourceFilters={calendarSourceFilters}
                                            />
                                        </div>
                                    </Col>
                                    <AnimatedSearchSiderPanel
                                        border
                                        open={!rightSiderCollapsed}
                                        position='right'
                                        siderLabel='Selected calendar day'
                                        style={{ height: '100%', minHeight: 0 }}
                                        width='22rem'
                                    >
                                        <CalendarSelectedDaySider selection={calendarSelection} />
                                    </AnimatedSearchSiderPanel>
                                </Row>
                            }
                        />
                        <Route
                            path='companies'
                            element={
                                <Companies
                                    filters={filters}
                                    handleChange={handleChange}
                                    leftSiderCollapsed={leftSiderCollapsed}
                                    matchMode={matchMode}
                                    onToggleLeftSider={toggleLeftSider}
                                    onToggleRightSider={toggleRightSider}
                                    onResultsFooterChange={reportResultsFooter}
                                    query={input}
                                    rightSiderCollapsed={rightSiderCollapsed}
                                    searchMode={searchMode}
                                />
                            }
                        />
                        <Route
                            path='economic'
                            element={
                                <FederatedSearchView
                                    domains={SCOPED_SEARCH_DOMAINS.economic}
                                    filters={filters}
                                    handleChange={handleChange}
                                    leftSiderCollapsed={leftSiderCollapsed}
                                    matchMode={matchMode}
                                    onToggleLeftSider={toggleLeftSider}
                                    onToggleRightSider={toggleRightSider}
                                    onResultsFooterChange={reportResultsFooter}
                                    query={input}
                                    rightSiderCollapsed={rightSiderCollapsed}
                                />
                            }
                        />
                        <Route
                            path='global-trade'
                            element={<Navigate to={`/search/${searchPreferences.defaultTab}`} replace />}
                        />
                        <Route
                            path='trade'
                            element={<Navigate to={`/search/${searchPreferences.defaultTab}`} replace />}
                        />
                    </Routes>
                </div>
            </KeyboardShortcutsWrapper>
        </SmallResourceFooterLayout>
    );
};

const Search: FC = (): JSX.Element => (
    <SearchCompanySyncProgressProvider>
        <SearchContent />
    </SearchCompanySyncProgressProvider>
);

export default Search;
